import type {
  Task,
  JournalEntry,
  MoodEntry,
  FocusSession,
  User,
  UserSettings,
  StatsSummary,
  ProposedTask,
  AIProvider,
  JournalType,
} from '@mindrift/shared';

const API_BASE = import.meta.env.VITE_API_URL ?? '/api';

let accessToken: string | null = null;
let refreshToken: string | null = null;
let refreshPromise: Promise<void> | null = null;
let onUnauthorized: (() => void) | null = null;

export function setAuthTokens(tokens: { accessToken: string; refreshToken: string } | null) {
  if (tokens) {
    accessToken = tokens.accessToken;
    refreshToken = tokens.refreshToken;
    localStorage.setItem('mindrift.refresh', tokens.refreshToken);
  } else {
    accessToken = null;
    refreshToken = null;
    localStorage.removeItem('mindrift.refresh');
  }
}

export function loadStoredRefresh(): string | null {
  refreshToken = localStorage.getItem('mindrift.refresh');
  return refreshToken;
}

export function setOnUnauthorized(handler: (() => void) | null) {
  onUnauthorized = handler;
}

export function getAccessToken(): string | null {
  return accessToken;
}

class ApiError extends Error {
  status: number;
  code?: string;
  constructor(status: number, message: string, code?: string) {
    super(message);
    this.status = status;
    this.code = code;
  }
}

export { ApiError };

async function refreshAccessToken(): Promise<void> {
  if (!refreshToken) throw new ApiError(401, 'No refresh token', 'UNAUTHENTICATED');
  if (refreshPromise) return refreshPromise;
  refreshPromise = (async () => {
    try {
      const res = await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken }),
      });
      if (!res.ok) throw new ApiError(res.status, 'Refresh failed');
      const data = await res.json();
      accessToken = data.accessToken;
      refreshToken = data.refreshToken;
      localStorage.setItem('mindrift.refresh', data.refreshToken);
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

async function request<T>(
  path: string,
  init: RequestInit = {},
  retry = true
): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(init.headers as Record<string, string> | undefined),
  };
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  const res = await fetch(`${API_BASE}${path}`, { ...init, headers });
  if (res.status === 401 && retry && refreshToken) {
    try {
      await refreshAccessToken();
      return request<T>(path, init, false);
    } catch (err) {
      onUnauthorized?.();
      throw new ApiError(401, 'Session expired', 'UNAUTHENTICATED');
    }
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: { message: 'Request failed' } }));
    throw new ApiError(res.status, err.error?.message ?? 'Request failed', err.error?.code);
  }
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export const api = {
  auth: {
    validateInvite: (inviteCode: string) =>
      request<{ valid: boolean }>('/auth/validate-invite', {
        method: 'POST',
        body: JSON.stringify({ inviteCode }),
      }),
    signup: (input: { inviteCode: string; email: string; password: string; name?: string }) =>
      request<{ accessToken: string; refreshToken: string; user: User }>('/auth/signup', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    login: (input: { email: string; password: string }) =>
      request<{ accessToken: string; refreshToken: string; user: User }>('/auth/login', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    logout: () =>
      request<{ ok: true }>('/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
      }),
    me: () => request<{ user: User }>('/auth/me'),
  },
  tasks: {
    list: (params?: { status?: string; area?: string }) => {
      const qs = new URLSearchParams();
      if (params?.status) qs.set('status', params.status);
      if (params?.area) qs.set('area', params.area);
      const q = qs.toString();
      return request<{ tasks: Task[] }>(`/tasks${q ? `?${q}` : ''}`);
    },
    create: (input: { title: string; description?: string | null; area?: string; dueDate?: string | null }) =>
      request<{ task: Task }>('/tasks', { method: 'POST', body: JSON.stringify(input) }),
    update: (id: string, input: Partial<{ title: string; description: string | null; area: string; dueDate: string | null; status: 'in_progress' | 'completed' | 'overdue' }>) =>
      request<{ task: Task }>(`/tasks/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    remove: (id: string) => request<{ ok: true }>(`/tasks/${id}`, { method: 'DELETE' }),
    trend: (days: number) => request<{ range: number; series: { date: string; added: number; completed: number; overdue: number }[] }>(`/tasks/trend?days=${days}`),
  },
  journal: {
    list: (params?: { type?: JournalType; area?: string; q?: string; limit?: number; cursor?: string }) => {
      const qs = new URLSearchParams();
      if (params?.type) qs.set('type', params.type);
      if (params?.area) qs.set('area', params.area);
      if (params?.q) qs.set('q', params.q);
      if (params?.limit) qs.set('limit', String(params.limit));
      if (params?.cursor) qs.set('cursor', params.cursor);
      const q = qs.toString();
      return request<{ entries: JournalEntry[]; nextCursor: string | null }>(`/journal${q ? `?${q}` : ''}`);
    },
    create: (input: { title?: string | null; body: string; type?: JournalType; area?: string | null }) =>
      request<{ entry: JournalEntry }>('/journal', { method: 'POST', body: JSON.stringify(input) }),
    update: (id: string, input: Partial<{ title: string | null; body: string; type: JournalType; area: string | null }>) =>
      request<{ entry: JournalEntry }>(`/journal/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    remove: (id: string) => request<{ ok: true }>(`/journal/${id}`, { method: 'DELETE' }),
    aiTidy: (input: { body: string; type?: JournalType }) =>
      request<{ cleaned: string; provider: AIProvider }>('/journal/ai-tidy', { method: 'POST', body: JSON.stringify(input) }),
    aiConvert: (input: { body: string; type?: JournalType; area?: string | null }) =>
      request<{ tasks: ProposedTask[]; provider: AIProvider }>('/journal/ai-convert', { method: 'POST', body: JSON.stringify(input) }),
    aiConvertCommit: (tasks: ProposedTask[]) =>
      request<{ created: Task[] }>('/journal/ai-convert/commit', { method: 'POST', body: JSON.stringify({ tasks }) }),
  },
  mood: {
    list: (params?: { from?: string; to?: string }) => {
      const qs = new URLSearchParams();
      if (params?.from) qs.set('from', params.from);
      if (params?.to) qs.set('to', params.to);
      const q = qs.toString();
      return request<{ entries: MoodEntry[] }>(`/mood${q ? `?${q}` : ''}`);
    },
    byDate: (date: string) => request<{ entry: MoodEntry | null }>(`/mood/${date}`),
    log: (input: { rating: number; note?: string | null; tags?: string[]; date?: string }) =>
      request<{ entry: MoodEntry }>('/mood', { method: 'POST', body: JSON.stringify(input) }),
    trend: (days: number) =>
      request<{ range: number; entries: { date: string; rating: number }[]; byArea: Record<string, number>; streak: number }>(`/mood/trend?days=${days}`),
    remove: (id: string) => request<{ ok: true }>(`/mood/${id}`, { method: 'DELETE' }),
  },
  focus: {
    list: (status?: 'running' | 'completed' | 'cancelled' | 'active') => {
      const q = status ? `?status=${status}` : '';
      return request<{ sessions: FocusSession[] }>(`/focus${q}`);
    },
    active: () => request<{ session: FocusSession | null }>('/focus/active'),
    start: (input: { duration: number; taskId?: string | null }) =>
      request<{ session: FocusSession }>('/focus', { method: 'POST', body: JSON.stringify(input) }),
    end: (id: string, input: { status: 'completed' | 'cancelled'; actualDuration?: number }) =>
      request<{ session: FocusSession }>(`/focus/${id}`, { method: 'PATCH', body: JSON.stringify(input) }),
    remove: (id: string) => request<{ ok: true }>(`/focus/${id}`, { method: 'DELETE' }),
  },
  stats: {
    get: (days: number) => request<StatsSummary & { range: number }>(`/stats?days=${days}`),
  },
  settings: {
    get: () => request<UserSettings>('/settings'),
    update: (input: { aiProvider?: AIProvider; aiApiKey?: string | null; customAreas?: string[]; defaultArea?: string }) =>
      request<UserSettings>('/settings', { method: 'PUT', body: JSON.stringify(input) }),
  },
};
